import { CurrentEntitlementPeriodResponse, SelectOption } from '../../services/dashboardService'
import { EnhancementDay, EnhancementGetResponse, SubmitStatus } from '../../models/enhancement'
import { ConflictMatch } from '../../types/base-response'
import { MyRequestDetailResponse } from '../../types/requests'
import { enhancementTypes, EntitlementPeriodType } from '../../utils/constants'
import { enhancementsList } from '../../utils/SharedMethods/isEnhancement'
import {
  findLatestCommentByStatus,
  latestApproverComment,
} from '../../utils/SharedMethods/getCommentsFromStatusHistory'

const editableRequestTypes = ['Holiday', 'Work From Home', 'Birthday', 'Day Off']

const nonEditableAmendedRequests = [...enhancementsList, 'Shift', 'Lieu']
const nonDeletableApprovedRequests = [
  'Holiday (M)',
  'Lieu (M)',
  'Adjustment',
  'Buy',
  'Sell',
  ...enhancementsList,
]

export function setYear(
  year: number,
  entitlement: CurrentEntitlementPeriodResponse | undefined
): SelectOption | null {
  if (!entitlement) {
    return null
  }

  const yearValue: SelectOption = {
    value: year,
    displayValue:
      entitlement.entitlementPeriod === 'CalendarYear' ? String(year) : `${year}/${year + 1}`,
    entitlementPeriod: entitlement.entitlementPeriod as EntitlementPeriodType,
  }

  return yearValue
}

export const canEditPendingRequest = (requestType: string, requestStatus?: string): boolean => {
  const isAmended = requestStatus && requestStatus.toLowerCase() === 'amended'

  if (requestType === enhancementTypes.callOut) return false
  return (
    editableRequestTypes.includes(requestType) ||
    (nonEditableAmendedRequests.includes(requestType) && !isAmended)
  )
}

export const calculateTotalHours = (requestDays: EnhancementDay[]) => {
  let total = 0
  requestDays.forEach(day => {
    total += day.checked ? Number(day.hours) : 0
  })
  return total
}

export const calculateTotalShifts = (
  requestDays: { checked: boolean; calculationCode?: string | null }[]
) => {
  let total = 0
  requestDays.forEach(day => {
    if (day.checked && day.calculationCode) {
      total += Number(day.calculationCode) || 0
    }
  })
  return total
}

export const transformEnhancementData = (
  data: EnhancementGetResponse
): MyRequestDetailResponse => ({
  currentStatus: data.currentStatus,
  requestId: data.id,
  type: data.enhancementType,
  employeeId: data.employeeDetails?.id ? data.employeeDetails.id : 0,
  dateFrom: data.dateRange.start!,
  dateTo: data.dateRange.end!,
  totalRequestedHours: calculateTotalHours(data.days!),
  requestDays: data.days!.map(({ day, hours, checked, calculationCode }, index) => ({
    day: day!,
    requestDayId: index + 1,
    hours: hours ? hours.toLocaleString() : '0',
    checked: checked!,
    calculationCode,
  })),
  requesterComments: findLatestCommentByStatus(data.statusHistory, SubmitStatus.PENDING),
  reviewerComments: latestApproverComment(data.statusHistory),
  ...(data.locationGroupId !== undefined ? { locationGroupId: data.locationGroupId } : {}),
  errors: data.errors,
})

export const canDeleteApprovedRequest = (requestType: string): boolean =>
  !nonDeletableApprovedRequests.includes(requestType)

/**
 * Detects whether a request detail has changed from its initial snapshot. Compares
 * top-level fields via toString (the original behaviour, which catches requesterComments
 * and totalRequestedHours changes) AND compares requestDays element-by-element so that
 * a per-day change that does not bubble up to a top-level field is still detected —
 * notably a checked flip on a coded On Call day where hours=0 and totalRequestedHours
 * stays 0. Returns false when either snapshot is null.
 */
export const requestDetailHasChanged = (
  current: MyRequestDetailResponse | null,
  initial: MyRequestDetailResponse | null
): boolean => {
  if (!current || !initial) return false

  const topLevelChanged = Object.keys(current).some(
    key =>
      current[key as keyof MyRequestDetailResponse]?.toString() !==
      initial[key as keyof MyRequestDetailResponse]?.toString()
  )
  if (topLevelChanged) return true

  const currentDays = current.requestDays ?? []
  const initialDays = initial.requestDays ?? []
  if (currentDays.length !== initialDays.length) return true

  return currentDays.some((day, index) => {
    const other = initialDays[index]
    return (
      day.checked !== other.checked ||
      day.hours !== other.hours ||
      day.calculationCode !== other.calculationCode ||
      day.day !== other.day ||
      day.requestDayId !== other.requestDayId
    )
  })
}

/**
 * Returns true if any checked day has hours === '0' and no calculationCode. A coded day
 * (e.g. US On Call with UseCalculationCodes) legitimately has hours = 0 — the calculation
 * code, not hours, drives payroll — so coded days are excluded. Used by ViewEditOccurrence
 * to guard the pending-request submit.
 */
export const hasZeroHourTickedDay = (
  days: { checked: boolean; hours: string; calculationCode?: string | null }[] | undefined
): boolean => !!days?.some(day => day.checked && day.hours === '0' && !day.calculationCode)

/**
 * Returns true if the request has a non-zero quantity to submit. For a coded On Call
 * (useShifts), the quantity is the count of shifts (totalShifts), since each coded day
 * carries hours=0. For non-coded requests, the quantity is totalHours. Used by
 * ViewEditOccurrence to guard the enhancement submit ("Hours are required").
 */
export const hasRequestedQuantity = (
  useShifts: boolean,
  totalShifts: number,
  totalHours: number
): boolean => (useShifts ? totalShifts > 0 : totalHours > 0)

/**
 * Determines whether a request-day text field should render in the error state.
 * Mirrors the original inline logic in ViewEditOccurrence: a checked day is invalid
 * when its hours are missing, zero, or greater than 24; and also flags when the day
 * falls on a conflict date from an alert.
 *
 * For coded On Call days (US On Call with UseCalculationCodes), hours=0 is the valid
 * state — the calculationCode drives payroll — so a checked coded day is never an
 * hours error.
 *
 * The day param uses a structural type so the same helper serves the employee side
 * (RequestDayRequest, hours: string) and the manager side (EnhancementDay, hours: number).
 */
export const requestDayHasFieldError = (
  day: { checked: boolean; hours: number | string; calculationCode?: string | null; day?: string },
  alerts: ConflictMatch[] | null
): boolean => {
  // A checked coded day (US On Call with UseCalculationCodes) legitimately has hours=0 —
  // the calculationCode drives payroll — so it is never an hours error.
  if (day.checked && day.calculationCode) return false

  const hoursNum = Number(day.hours)
  const hasInvalidHours = !day.hours || hoursNum === 0 || hoursNum > 24
  if (!alerts) {
    return (day.checked && hasInvalidHours) || false
  }
  return (
    (day.checked && hasInvalidHours) ||
    alerts.some(alert => alert.conflictDates.some(fdate => fdate, day.day))
  )
}
